import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import { Helmet } from "react-helmet-async";

const PROD_DOMAIN = "https://www.thesecretseller.co.uk";
const PRICE_PER_IMAGE = 55;

export default function ROICalculatorPage() {
  const [propertyValue, setPropertyValue] = useState("");
  const [expectedSalePrice, setExpectedSalePrice] = useState("");
  const [rooms, setRooms] = useState("");
  const [calculated, setCalculated] = useState(false);

  const numRooms = parseInt(rooms) || 0;
  const propVal = parseFloat(propertyValue) || 0;
  const salePrice = parseFloat(expectedSalePrice) || 0;

  const stagingCost = numRooms * PRICE_PER_IMAGE;
  const uplift = salePrice - propVal;
  const upliftPercent = propVal > 0 ? ((uplift / propVal) * 100).toFixed(1) : 0;
  const roi = stagingCost > 0 ? Math.round((uplift / stagingCost) * 100) : 0;
  const costPerEnquiry = stagingCost > 0 ? (stagingCost / Math.max(numRooms * 3, 1)).toFixed(2) : 0;

  const handleCalculate = (e) => {
    e.preventDefault();
    if (numRooms > 0 && propVal > 0) setCalculated(true);
  };

  const handleReset = () => {
    setPropertyValue("");
    setExpectedSalePrice("");
    setRooms("");
    setCalculated(false);
  };

  return (
    <main data-testid="roi-calculator-page" className="pt-24">
      <Helmet>
        <title>Virtual Staging ROI Calculator | The Secret Seller</title>
        <meta name="description" content="Calculate the return on investment from virtual staging. Enter your property value, expected sale price and number of rooms to see your potential uplift." />
        <link rel="canonical" href={`${PROD_DOMAIN}/virtual-staging-roi-calculator`} />
      </Helmet>

      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">Free Tool</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-[2.8rem] font-extralight text-[#0D2D54] leading-[1.1] tracking-[-0.01em] mb-6">
            Virtual Staging ROI Calculator
          </h1>
          <p className="text-[15px] text-[#6B7280] leading-[1.8] max-w-xl mx-auto">
            See how much virtual staging could save you compared to physical staging — and what it could add to your sale price.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Calculator Form */}
            <div>
              <form onSubmit={handleCalculate} className="space-y-6">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">
                    Current Property Value (&pound;)
                  </label>
                  <input
                    type="number"
                    data-testid="input-property-value"
                    value={propertyValue}
                    onChange={(e) => { setPropertyValue(e.target.value); setCalculated(false); }}
                    placeholder="e.g. 300000"
                    className="w-full border border-[#E8E8E4] px-4 py-3.5 text-[15px] text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">
                    Expected Sale Price After Staging (&pound;)
                  </label>
                  <input
                    type="number"
                    data-testid="input-sale-price"
                    value={expectedSalePrice}
                    onChange={(e) => { setExpectedSalePrice(e.target.value); setCalculated(false); }}
                    placeholder="e.g. 315000"
                    className="w-full border border-[#E8E8E4] px-4 py-3.5 text-[15px] text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">
                    Number of Rooms to Stage
                  </label>
                  <input
                    type="number"
                    data-testid="input-rooms"
                    value={rooms}
                    onChange={(e) => { setRooms(e.target.value); setCalculated(false); }}
                    placeholder="e.g. 6"
                    min="1"
                    className="w-full border border-[#E8E8E4] px-4 py-3.5 text-[15px] text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="calculate-btn"
                  className="w-full bg-[#0D2D54] text-white py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all flex items-center justify-center gap-2"
                >
                  <Calculator size={16} />
                  Calculate My ROI
                </button>
              </form>

              {calculated && (
                <button
                  onClick={handleReset}
                  className="w-full mt-3 text-xs text-[#6B7280] hover:text-[#0D2D54] transition-colors text-center py-2"
                >
                  Reset calculator
                </button>
              )}
            </div>

            {/* Results */}
            <div>
              {calculated ? (
                <div data-testid="roi-results" className="space-y-6">
                  <div className="bg-[#FAFAF8] border border-[#E8E8E4] p-6">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">Cost of Virtual Staging</p>
                    <p className="font-['Outfit'] text-3xl font-extralight text-[#0D2D54]">
                      &pound;{stagingCost.toLocaleString()}
                    </p>
                    <p className="text-xs text-[#6B7280] mt-1">{numRooms} rooms &times; &pound;{PRICE_PER_IMAGE} per image</p>
                  </div>

                  <div className="bg-[#FAFAF8] border border-[#E8E8E4] p-6">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">Potential Uplift</p>
                    <p className="font-['Outfit'] text-3xl font-extralight text-[#0D2D54]">
                      &pound;{uplift.toLocaleString()}
                    </p>
                    <p className="text-xs text-[#758C7B] mt-1 font-medium">{upliftPercent}% increase on property value</p>
                  </div>

                  <div className="bg-[#FAFAF8] border border-[#E8E8E4] p-6">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">Return on Investment</p>
                    <p className="font-['Outfit'] text-3xl font-extralight text-[#0D2D54]">
                      {roi > 0 ? `${roi.toLocaleString()}%` : "—"}
                    </p>
                    <p className="text-xs text-[#6B7280] mt-1">For every &pound;1 spent on staging</p>
                  </div>

                  <div className="bg-[#FAFAF8] border border-[#E8E8E4] p-6">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2 font-medium">Estimated Cost Per Enquiry</p>
                    <p className="font-['Outfit'] text-3xl font-extralight text-[#0D2D54]">
                      &pound;{costPerEnquiry}
                    </p>
                    <p className="text-xs text-[#6B7280] mt-1">Based on average enquiry uplift from staged listings</p>
                  </div>

                  <div className="bg-[#0D2D54] p-6 text-center">
                    <p className="text-xs text-white/60 mb-1">Compared to physical staging</p>
                    <p className="text-white font-['Outfit'] text-lg font-light">
                      You save &pound;{Math.max(2000 - stagingCost, 0).toLocaleString()}+
                    </p>
                    <p className="text-xs text-white/50 mt-1">Physical staging typically costs &pound;2,000 — &pound;5,000</p>
                  </div>

                  <Link
                    to="/order"
                    data-testid="roi-cta"
                    className="block text-center bg-[#0D2D54] text-white py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all"
                  >
                    Send Us Your Photos →
                  </Link>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-[#6B7280]">
                    <Calculator size={40} className="mx-auto mb-4 text-[#E8E8E4]" />
                    <p className="text-sm">Enter your property details to see your potential return on investment.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Context Section */}
      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-6 text-center">How virtual staging impacts property sales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            {[
              { stat: "73%", label: "faster time to offer" },
              { stat: "40%", label: "more portal clicks" },
              { stat: "20%", label: "higher perceived value" },
            ].map((item, i) => (
              <div key={i} className="text-center bg-white p-6 border border-[#E8E8E4]">
                <p className="font-['Outfit'] text-3xl font-extralight text-[#0D2D54]">{item.stat}</p>
                <p className="text-xs text-[#6B7280] mt-1">{item.label}</p>
              </div>
            ))}
          </div>
          <p className="text-[15px] text-[#6B7280] leading-[1.8] text-center">
            Properties marketed with virtual staging consistently outperform empty equivalents — generating more clicks, more viewings and stronger offers. At just &pound;55 per image, it's one of the highest-ROI investments in property marketing.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-white mb-6">Ready to see the difference?</h2>
          <p className="text-[15px] text-white/50 mb-10">Send us your photos and we'll return beautifully staged images within 48 hours.</p>
          <Link to="/order" className="inline-flex items-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all">
            Send Us Your Photos <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
